/**
 * Frontend-seitige Bild-Komprimierung VOR Upload.
 *
 * Spart Bandbreite (relevant auf Mobile-Funknetz) und entlastet den Server,
 * weil sharp dann nur noch eine kleinere Datei verarbeiten muss.
 *
 * Strategie:
 *   - Quelle > 2048 px Längste Kante → resizen
 *   - Quelle > 2 MB nach JPEG-Komprimierung → quality runter
 *   - Output: dasselbe Original-Format behalten (JPEG bleibt JPEG, PNG bleibt PNG —
 *     der Server konvertiert sowieso nochmal zu WebP)
 *
 * Falls die Komprimierung scheitert (z.B. Browser ohne Canvas-Support):
 * Original-File zurückgeben — der Server-Pfad funktioniert auch dann.
 */

import imageCompression from 'browser-image-compression';

export interface CompressionResult {
  file: File;
  /** True wenn die Datei tatsächlich verkleinert wurde, false wenn unverändert */
  wasCompressed: boolean;
  /** Größe vor/nach Komprimierung (für UX-Hinweis "von 8 MB auf 0.6 MB") */
  originalSize: number;
  finalSize: number;
}

const MAX_DIMENSION = 2048;
const MAX_TARGET_MB = 2;

export async function compressImage(file: File): Promise<CompressionResult> {
  const originalSize = file.size;

  // SVG / GIF: nicht komprimieren — Animation/Vektor-Eigenschaften gehen sonst verloren.
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') {
    return { file, wasCompressed: false, originalSize, finalSize: originalSize };
  }

  try {
    const compressed = await imageCompression(file, {
      maxSizeMB: MAX_TARGET_MB,
      maxWidthOrHeight: MAX_DIMENSION,
      useWebWorker: true,
      // Original-Format behalten (server konvertiert sowieso zu WebP)
      fileType: file.type,
      // Initial-Qualität — wird bei Bedarf reduziert um maxSizeMB zu erreichen
      initialQuality: 0.85
    });

    // Wenn das Ergebnis größer als Original wäre (kann passieren bei sehr
    // kleinen Bildern), Original behalten.
    if (compressed.size >= originalSize) {
      return { file, wasCompressed: false, originalSize, finalSize: originalSize };
    }

    // Library liefert Blob, wir wollen File mit Original-Namen
    const finalFile = new File([compressed], file.name, {
      type: compressed.type || file.type,
      lastModified: Date.now()
    });

    return {
      file: finalFile,
      wasCompressed: true,
      originalSize,
      finalSize: finalFile.size
    };
  } catch (err) {
    console.warn('[imageCompression] failed, sending original:', err);
    return { file, wasCompressed: false, originalSize, finalSize: originalSize };
  }
}
